import { computed, reactive, ref } from 'vue';
import { HttpStatusCode } from 'axios';
import { useHttp, useToasts } from '@/composables';
import { PlanWorkflowStep } from '@/const';

const {
  WorkInProgress1, WorkInProgress2, WorkInProgress3,
  SubmittedForCouncilNoting, SubmittedForMecReview,
  DraftApproval, SubmittedForCouncilApproval,
  SubmittedToMec, FinalApproval,
} = PlanWorkflowStep;

const REJECT_TITLES = {
  [DraftApproval]: 'Reject this draft?',
  [SubmittedForCouncilApproval]: 'Reject the final document?',
  [FinalApproval]: 'Reject the final document?',
};

const NOTIFY_ADMIN_DESCRIPTIONS = {
  [WorkInProgress1]: 'The document will enter the publication workflow.',
  [WorkInProgress2]: 'The document will be published as a draft. If further changes are required, it will revert to the previous Work in Progress phase.',
  [WorkInProgress3]: 'The document will be published as a final document. If further changes are required, it will revert to the previous Work in Progress phase.',
};

export const useWorkflowModals = (workflowStep, loadPlan, municipalityId) => {
  const toasts = useToasts();

  const rejectModalTitle = computed(() => REJECT_TITLES[workflowStep.value]);
  const notifyAdminDescription = computed(() => NOTIFY_ADMIN_DESCRIPTIONS[workflowStep.value]);

  const post = async (endpoint, show, processing, { success, error }) => {
    processing.value = true;

    const response = await useHttp().post(`/municipalities/${municipalityId}/plan/workflow/${endpoint}`);

    if (response.statusCode === HttpStatusCode.Ok) {
      await loadPlan();
      toasts.success(success);
    } else {
      toasts.error(error);
    }

    show.value = false;
    processing.value = false;
  };

  /**
   * @param {string|Object} endpoint - API endpoint string, or a step-keyed map
   * @param {{ success: string, error: string }} messages - Toast messages on success or failure
   */
  const makeModal = (endpoint, messages) => {
    const show = ref(false);
    const processing = ref(false);

    return {
      show,
      processing,
      open: () => show.value = true,
      confirm: () => post(
        typeof endpoint === 'object' ? endpoint[workflowStep.value] : endpoint,
        show,
        processing,
        messages,
      ),
    };
  };

  const modals = {
    // IDP Officer/Municipal Administrator — wip1→council_noting, wip2→draft_approval, wip3→council_approval
    notifyAdmin: makeModal(
      { [WorkInProgress1]: 'submit-for-noting', [WorkInProgress2]: 'submit-for-draft-approval', [WorkInProgress3]: 'submit-for-council-approval' },
      { success: 'Submitted successfully', error: 'Could not submit' },
    ),
    // Municipal Administrator — council_noting→mec_review
    approveDraftForCouncil: makeModal(
      'approve-noting',
      { success: 'Draft approved for Council noting', error: 'Could not approve draft for Council noting' },
    ),
    // Municipal Administrator — mec_review→wip2
    submitForFirstMec: makeModal(
      'approve-mec-review',
      { success: 'MEC submission confirmed', error: 'Could not confirm MEC submission' },
    ),
    // Municipal Administrator — draft_approval→wip3
    approveDraft: makeModal(
      'approve-draft-approval',
      { success: 'Draft approved and published', error: 'Could not approve draft' },
    ),
    // Municipal Administrator — council_approval→mec_approval
    approveFinalForCouncil: makeModal(
      'approve-council-approval',
      { success: 'Final approved for Council', error: 'Could not approve final for Council' },
    ),
    // Municipal Administrator — mec_approval→final_approval
    submitForFinalMec: makeModal(
      'approve-mec-approval',
      { success: 'MEC submission confirmed', error: 'Could not confirm MEC submission' },
    ),
    // Municipal Administrator — final_approval→published_as_final
    approveFinalAndPublish: makeModal(
      'approve-final-approval',
      { success: 'Final document approved and published', error: 'Could not approve final document' },
    ),
    // Municipal Administrator — draft_approval→wip2, council_approval→wip3, final_approval→wip3
    reject: makeModal(
      { [DraftApproval]: 'reject-draft-approval', [SubmittedForCouncilApproval]: 'reject-council-approval', [FinalApproval]: 'reject-final-approval' },
      { success: 'Document rejected', error: 'Could not reject document' },
    ),
    // Municipal Administrator — council_noting→wip1, mec_review→wip1, mec_approval→wip3
    reopenForEditing: makeModal(
      { [SubmittedForCouncilNoting]: 'reject-noting', [SubmittedForMecReview]: 'reject-mec-review', [SubmittedToMec]: 'reject-mec-approval' },
      { success: 'Document re-opened for editing', error: 'Could not re-open document for editing' },
    ),
  };

  return {
    modals: reactive(modals),
    rejectModalTitle,
    notifyAdminDescription,
  };
};
